import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import MUIDataTable from 'mui-datatables';
import {
  Typography,
  Grid,
  Button,
  Tooltip,
  InputLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import {
  longDateTimeFormat,
  VehicleEventTypes,
  VEHICLE_TYPES,
  OpsBrandNames,
  timeZoneHint,
  vehicleZoneCategories,
  VehicleZonesStatusNames,
  shouldFetchList,
} from '../../../lib/utils';
import vehicleActions from '../../../actions/vehicles';
import rideActions from '../../../actions/rides';
import vehicleZoneActions from '../../../actions/vehicleZones';
import GeoCoordinatesDisplay from '../../common/geo-coordinates';
import { tableOptions } from '../../../lib/table-utils';
import HistoryMap from '../map/history/history-map';
import { RouteMapContainer } from '../map/route-map-container';
import WrappedSpinner from '../../common/wrapped-spinner';
import VehicleStats from './vehicleStats';
import MultiSelect from '../../common/multi-select';

const TABLE_VIEW = 0;
const FULL_LOCATIONS_MAP_VIEW = 1;
const SINGLE_LOCATION_MAP_VIEW = 2;
const ROUTE_MAP_VIEW = 3;

const DISPLAY_BATTERY_TYPES = [VEHICLE_TYPES.EBIKE, VEHICLE_TYPES.SCOOTER];

class VehicleHistory extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      vehicleStatsLoading: true,
      vehicleHistoryLoading: true,
      activeView: TABLE_VIEW,
      tripId: undefined,
      vehicleHistoryIdx: undefined,
      filterCategory: [],
    };

    this.getTableData = this.getTableData.bind(this);
    this.getTableColumns = this.getTableColumns.bind(this);
    this.getTableOptions = this.getTableOptions.bind(this);
    this.fetchHistoryData = this.fetchHistoryData.bind(this);
  }

  componentDidMount() {
    this.fetchHistoryData();
  }

  getTableColumns() {
    const {
      vehicle, vehicleHistory, market,
    } = this.props;
    const vehicleType = get(vehicle, 'type');
    const vehicleBrand = get(vehicle, 'brand');
    const marketTz = get(market, 'tz', 'America/New_York');

    let columns = [
      {
        name: 'id',
        options: {
          display: 'excluded',
        },
      }, {
        name: 'Location #',
      }, {
        name: 'Date/Time',
        options: {
          customBodyRender: (dateString) => (
            moment.tz(dateString, marketTz).format(longDateTimeFormat)
          ),
          hint: timeZoneHint(marketTz),
        },
      }, {
        name: 'Action',
      }, {
        name: 'Wrangler/Rider',
      }, {
        name: 'New Status',
        options: {
          customBodyRender: (statuses) => {
            if (statuses) {
              return (
                <Grid>
                  {statuses.map((s) => s && (
                    <Typography key={s}>
                      {s}
                    </Typography>
                  ))}
                </Grid>
              );
            } else {
              return '';
            }
          },
        },
      },
    ];
    columns = columns.concat([
      {
        name: 'Trip',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (tripId) => {
            if (tripId && tripId.length) {
              return (
                <Tooltip title="Click to see trip route on map">
                  <Typography>
                    <Button
                      color="primary"
                      onClick={() => {
                        this.setState(() => ({ tripId, activeView: ROUTE_MAP_VIEW }));
                      }}
                    >
                      View Trip
                    </Button>
                  </Typography>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
        },
      }, {
        name: 'Geolocation',
        options: {
          customBodyRender: (vehicleHistoryIdx) => {
            if ((vehicleHistoryIdx || vehicleHistoryIdx === 0) && get(vehicleHistory, `${vehicleHistoryIdx}.geo`)) {
              const geo = get(vehicleHistory, `${vehicleHistoryIdx}.geo`, {});
              const coordinates = [geo.longitude, geo.latitude];
              return (
                <GeoCoordinatesDisplay
                  onClick={() => this.setState(() => ({
                    vehicleHistoryIdx,
                    activeView: SINGLE_LOCATION_MAP_VIEW,
                  }))}
                  coordinates={coordinates}
                />
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'Task',
      },
    ]);
    if (DISPLAY_BATTERY_TYPES.includes(vehicleType)) columns.push({ name: 'Battery Level (%)' });
    if (vehicleBrand === OpsBrandNames.LINK && vehicleType === VEHICLE_TYPES.SCOOTER) columns.push({ name: 'Odometer (km)' });

    return columns;
  }

  getTableData() {
    const { vehicle, vehicleHistory } = this.props;

    if (!vehicleHistory) return [];
    const vehicleType = get(vehicle, 'type');
    const vehicleBrand = get(vehicle, 'brand');

    const dataResult = vehicleHistory.map((vehicleEvent, idx) => {
      const {
        _id, createdAt, reason, userName, taskId,
        batteryLevel, odometerKMs,
      } = vehicleEvent;

      const tripId = get(vehicleEvent, 'tripId', '');
      const deploymentStatus = get(vehicleEvent, 'vehicleExtDeploymentStatus', '');
      const possessionStatus = get(vehicleEvent, 'vehicleExtPossessionStatus', '');
      const extStatus = get(vehicleEvent, 'vehicleExtStatus', '');
      const statuses = [extStatus, deploymentStatus, possessionStatus];

      let rowData = [
        _id,
        idx,
        createdAt,
        reason,
        userName,
        statuses,
      ];

      if (reason !== VehicleEventTypes.START_RIDE) rowData.push(tripId);
      else rowData.push(null);

      rowData = rowData.concat([
        idx,
        taskId,
      ]);
      if (DISPLAY_BATTERY_TYPES.includes(vehicleType)) {
        // get battery from event; default to blank
        let battery = batteryLevel;
        if (isUndefined(battery)) battery = '';

        rowData.push(battery);
      }
      if (vehicleBrand === OpsBrandNames.LINK && vehicleType === VEHICLE_TYPES.SCOOTER) {
        // get odometer from event; default to blank
        let odometer = odometerKMs;
        if (isUndefined(odometer)) odometer = '';
        else odometer = odometer.toFixed(2);
        rowData.push(odometer);
      }

      return rowData;
    });

    return dataResult;
  }

  getTableOptions() {
    const { tableOptions: basicOptions } = this.props;

    let options = basicOptions;
    if (!options) options = {};

    options = { ...tableOptions, ...options };
    options.selectableRows = 'none';
    options.rowsPerPageOptions = [25, 50, 100, 250, 1000];
    options.rowsPerPage = 100;

    return options;
  }

  fetchHistoryData() {
    const {
      vehicle, fetchVehicleHistory, fetchRidesByVehicleId,
      match, vehicleZones, fetchVehicleZones,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    if (marketId) {
      if (shouldFetchList(marketId, vehicleZones)) fetchVehicleZones(marketId);
    }
    if (vehicle) {
      // set Loading states; do fetch; unset Loading states
      this.setState(
        () => ({ vehicleHistoryLoading: true, vehicleStatsLoading: true }),
        () => {
          fetchVehicleHistory(vehicle._id)
            .then(() => this.setState(() => ({ vehicleHistoryLoading: false })));
          fetchRidesByVehicleId(vehicle._id)
            .then(() => {
              this.setState(() => ({ vehicleStatsLoading: false }));
            });
        },
      );
    }
  }

  handleCategoryFilter(value) {
    this.setState({ filterCategory: value });
  }

  render() {
    const {
      vehicleZones = [],
      vehicleHistory,
      vehicleRides,
      vehicleActivity,
      market,
    } = this.props;
    const {
      activeView,
      tripId,
      vehicleHistoryIdx,
      vehicleStatsLoading,
      vehicleHistoryLoading,
      filterCategory,
    } = this.state;

    const tableData = this.getTableData();
    let singleEvent;
    if (activeView === SINGLE_LOCATION_MAP_VIEW) {
      singleEvent = get(vehicleHistory, vehicleHistoryIdx);
      singleEvent.index = vehicleHistoryIdx;
    }

    const isLoading = vehicleStatsLoading || vehicleHistoryLoading;
    const marketTz = get(market, 'tz', 'America/New_York');
    const zonesToDisplay = vehicleZones.filter(
      (zone) => zone.status === VehicleZonesStatusNames.ACTIVE
        && filterCategory
        && filterCategory.includes(zone.category),
    );

    return (
      <div>
        <Grid container>
          <VehicleStats
            isLoading={vehicleStatsLoading}
            vehicleRides={vehicleRides}
            vehicleActivity={vehicleActivity}
          />
          <Grid item xs={12}>
            <Grid container sx={{ paddingBottom: 1 }}>
              <Grid item md={4}>
                {activeView === ROUTE_MAP_VIEW && (
                  <>
                    <InputLabel>Map Layers</InputLabel>
                    <MultiSelect
                      value={filterCategory}
                      options={vehicleZoneCategories.map((c) => ({
                        value: c,
                        label: c,
                        isDisabled: false,
                      }))}
                      handleChange={(value) => this.handleCategoryFilter(value)}
                    />
                  </>
                )}
              </Grid>
              <Grid item xs={10} />
              {activeView === SINGLE_LOCATION_MAP_VIEW && (
                <Button
                  color="primary"
                  onClick={() => this.setState(() => ({
                    activeView: TABLE_VIEW,
                    vehicleHistoryIdx: undefined,
                  }))}
                >
                  Close map
                </Button>
              )}
              {activeView === FULL_LOCATIONS_MAP_VIEW && (
                <Button
                  color="primary"
                  onClick={() => this.setState(() => ({ activeView: TABLE_VIEW }))}
                >
                  Close map
                </Button>
              )}
              {activeView === ROUTE_MAP_VIEW && (
                <Button
                  color="primary"
                  onClick={() => this.setState(() => ({
                    activeView: TABLE_VIEW,
                    tripId: undefined,
                  }))}
                >
                  Close map
                </Button>
              )}
              {activeView === TABLE_VIEW && (
                <Button
                  color="primary"
                  onClick={() => this.setState(() => ({ activeView: FULL_LOCATIONS_MAP_VIEW }))}
                >
                  Open map
                </Button>
              )}
              <Grid item xs={1}>
                {!isLoading && (
                  <Tooltip
                    title="Click to refresh the data"
                    onClick={this.fetchHistoryData}
                  >
                    <Button variant="contained" color="primary">
                      Reload
                      <RefreshIcon />
                    </Button>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {activeView === SINGLE_LOCATION_MAP_VIEW && (
          <div>
            <HistoryMap vehicleEvents={[singleEvent]} tz={marketTz} />
          </div>
        )}
        {activeView === ROUTE_MAP_VIEW && (
          <div>
            <RouteMapContainer tripId={tripId} zones={zonesToDisplay} />
          </div>
        )}
        {activeView === FULL_LOCATIONS_MAP_VIEW && (
          <div>
            <HistoryMap vehicleEvents={vehicleHistory} tz={marketTz} />
          </div>
        )}
        {activeView === TABLE_VIEW && (
          <WrappedSpinner isLoading={vehicleHistoryLoading}>
            <MUIDataTable
              title="Vehicle History"
              data={tableData}
              columns={this.getTableColumns()}
              options={this.getTableOptions()}
            />
          </WrappedSpinner>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...vehicleActions,
  ...rideActions,
  ...vehicleZoneActions,
}, dispatch);

const mapStateToProps = ({
  vehicles, markets, rides, vehicleZones,
}, { vehicle }) => ({
  vehicles: vehicles.marketVehicles,
  vehicleHistory: vehicles.currentVehicleHistory,
  vehicleActivity: get(vehicles, `activities.${get(vehicle, 'marketId')}.${get(vehicle, '_id')}`),
  market: markets.selectedMarket,
  vehicleRides: rides.currentVehicleRides,
  vehicleZones: vehicleZones.vehicleZones,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(VehicleHistory));
